import React from 'react';
import { Box, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import SiteLink from '../global/SiteLink';
import SocialIcon from '../global/SocialIcon';
import { useSite } from '../../site/SiteContext';

// Every text, link and image comes from the dashboard: Header & footer →
// Footer, Menu links (footer quick links), Services, Settings (contact) and Social links.
const Footer = () => {
    const { site, locale: lng } = useSite();
    const texts = site.layout?.footer ?? {};
    const contact = site.contact ?? {};
    const currentYear = new Date().getFullYear();

    const textPrimary = "rgba(255,255,255,0.78)";
    const textAccent = "#D6BC58";

    return (
        <footer style={{ overflow: 'hidden' }}>
            <Box sx={{
                position: 'relative',
                overflow: 'hidden',
                backgroundColor: '#1c1a17',
                color: '#fff'
            }}>
                {/* Background Image */}
                <Box sx={{
                    position: 'absolute',
                    inset: 0,
                    pointerEvents: 'none',
                    '& img': {
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        objectPosition: 'bottom',
                        opacity: 0.56,
                        position: 'absolute',
                        left: 0,
                        top: 0
                    }
                }}>
                    {texts.background?.url && <img src={texts.background.url} alt="" loading="lazy" />}
                </Box>

                <Box sx={{
                    position: 'relative',
                    zIndex: 10,
                    mx: 'auto',
                    maxWidth: '1280px',
                    px: { xs: 2, lg: 4 },
                    pb: { xs: 5, lg: 6 },
                    pt: { xs: 7, lg: 10 }
                }}>
                    <Box sx={{
                        display: 'grid',
                        gap: 4,
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(2, 1fr)',
                            lg: 'repeat(5, 1fr)',
                            xl: 'repeat(5, 1fr)'
                        }
                    }}>
                        {/* Column 1: Logo & Info */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '3px', textDecoration: 'none' }}>
                                {site.brand?.logo?.url && <img src={site.brand.logo.url} alt={site.brand?.name} loading="lazy" width="52" height="52" style={{ objectFit: 'contain' }} />}
                                <Box sx={{ display: 'flex', flexDirection: lng === 'ar' ? 'row' : 'row-reverse', alignItems: 'center', fontWeight: 'bold' }}>
                                    <span style={{ color: '#D6BC58', fontSize: '18px' }}>{site.brand?.name}</span>
                                </Box>
                            </Link>
                            <Typography sx={{ fontSize: '0.98rem', fontWeight: 600, lineHeight: 1.6, color: textPrimary }}>
                                {texts.tagline}
                            </Typography>
                        </Box>

                        {/* Column 2: Quick Links */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: textAccent }}>
                                {texts.quick_links_title}
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {site.menus?.footer?.map((link, idx) => (
                                    <SiteLink key={idx} to={link.url} newTab={link.new_tab} style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)', textDecoration: 'none', transition: 'color 0.2s' }}
                                        onMouseEnter={(e) => e.target.style.color = textAccent}
                                        onMouseLeave={(e) => e.target.style.color = 'rgba(255,255,255,0.7)'}>
                                        {link.label}
                                    </SiteLink>
                                ))}
                            </Box>
                        </Box>

                        {/* Column 3: Services */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: textAccent }}>
                                {texts.services_title}
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {site.services?.map((service) => (
                                    <Link key={service.slug} to={service.url} style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)', textDecoration: 'none', transition: 'color 0.2s' }}
                                        onMouseEnter={(e) => e.target.style.color = textAccent}
                                        onMouseLeave={(e) => e.target.style.color = 'rgba(255,255,255,0.7)'}>
                                        {service.title}
                                    </Link>
                                ))}
                            </Box>
                        </Box>

                        {/* Column 4: Contact Us */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: textAccent }}>
                                {texts.contact_title}
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {contact.phones?.map((phone) => (
                                    <a key={phone} href={`tel:${phone}`} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.98rem', fontWeight: 600, color: textPrimary, textDecoration: 'none', transition: 'color 0.2s' }}
                                        onMouseEnter={(e) => e.currentTarget.style.color = textAccent}
                                        onMouseLeave={(e) => e.currentTarget.style.color = textPrimary}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                        <path d="M13.832 16.568a1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233 14 14 0 0 0 6.392 6.384"></path>
                                    </svg>
                                        <span dir="ltr" style={{ pointerEvents: 'none' }}>{phone}</span>
                                    </a>
                                ))}
                                {contact.email && (
                                    <a href={`mailto:${contact.email}`} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.98rem', fontWeight: 600, color: textPrimary, textDecoration: 'none', transition: 'color 0.2s' }}
                                        onMouseEnter={(e) => e.currentTarget.style.color = textAccent}
                                        onMouseLeave={(e) => e.currentTarget.style.color = textPrimary}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                        <path d="m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7"></path>
                                        <rect x="2" y="4" width="20" height="16" rx="2"></rect>
                                    </svg>
                                        <span style={{ pointerEvents: 'none' }}>{contact.email}</span>
                                    </a>
                                )}
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '0.98rem', fontWeight: 600, color: textPrimary }}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: '4px' }} aria-hidden="true">
                                        <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"></path>
                                        <circle cx="12" cy="10" r="3"></circle>
                                    </svg>
                                    <span>
                                        {contact.address}
                                    </span>
                                </div>
                            </Box>
                            {site.social?.length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: textAccent, mb: 1 }}>
                                        {texts.follow_title}
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {site.social.map((link) => (
                                            <Box key={link.url} component="a" href={link.url} target="_blank" rel="noopener noreferrer" aria-label={link.label}
                                                sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: '0.75rem', border: '1px solid rgba(255,255,255,0.16)', color: textPrimary, fontSize: '1.1rem', transition: 'all 0.2s', '&:hover': { color: '#1c1a17', backgroundColor: textAccent } }}>
                                                <SocialIcon platform={link.platform} />
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            )}
                        </Box>

                        {/* Column 5: QR Code */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, justifySelf: { xl: 'flex-end' }, alignItems: 'center' }}>
                            <Box sx={{
                                display: 'inline-flex',
                                overflow: 'hidden',
                                borderRadius: '1.5rem',
                                border: '1px solid rgba(255,255,255,0.16)',
                                backgroundColor: 'rgba(255,255,255,0.96)',
                                p: 1,
                                boxShadow: '0 24px 50px -30px rgba(0,0,0,0.55)',
                            }}>
                                {texts.qr?.url && <img src={texts.qr.url} alt="QR Code" loading="lazy" width="168" height="168" style={{ width: '10rem', height: '10rem', borderRadius: '1.1rem', objectFit: 'contain' }} />}
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* Bottom Bar */}
            <Box sx={{ borderTop: '1px solid #E5E7EB', backgroundColor: '#fff', color: '#111' }}>
                <Box sx={{
                    mx: 'auto',
                    maxWidth: '1280px',
                    px: { xs: 2, lg: 4 },
                    py: 1.5,
                }}>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        alignItems: 'center',
                        gap: 1,
                        textAlign: 'center',
                        justifyContent: 'space-between'
                    }}>
                        <Typography sx={{ fontSize: '0.75rem', color: '#6B7280', justifySelf: { md: 'flex-start' } }}>
                            © {currentYear} {site.brand?.name}. {texts.copyright}.
                        </Typography>

                        <Box sx={{ fontSize: '0.75rem', color: '#6B7280', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            {texts.powered_by_label}
                            <a href={texts.powered_by_url} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 600, color: '#111', textDecoration: 'none', transition: 'color 0.2s' }}
                                onMouseEnter={(e) => e.currentTarget.style.color = textAccent}
                                onMouseLeave={(e) => e.currentTarget.style.color = '#111'}>
                                {texts.powered_by_name}
                            </a>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifySelf: { md: 'flex-end' } }}>
                            <Link to="/privacy-policy" style={{ fontSize: '0.75rem', color: '#6B7280', textDecoration: 'none', transition: 'color 0.2s' }}
                                onMouseEnter={(e) => e.target.style.color = textAccent}
                                onMouseLeave={(e) => e.target.style.color = '#6B7280'}>
                                {texts.privacy_label}
                            </Link>
                            <Link to="/terms-and-conditions" style={{ fontSize: '0.75rem', color: '#6B7280', textDecoration: 'none', transition: 'color 0.2s' }}
                                onMouseEnter={(e) => e.target.style.color = textAccent}
                                onMouseLeave={(e) => e.target.style.color = '#6B7280'}>
                                {texts.terms_label}
                            </Link>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </footer>
    );
};

export default Footer;
